import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const flytthjalpLidingoPage: ServicePageConfig = {
  slug: "flytthjalp-lidingo",
  serviceCategory: "flytt",
  city: "stockholm",

  meta: {
    title: "Flytthjälp Lidingö – Bil och bärare över bron | Ungdomskraft",
    description:
      "Boka flytthjälp på Lidingö. Vi flyttar villor och lägenheter i Brevik, Käppala, Larsberg, Hersby och Gåshaga. Fast paketpris och RUT-avdrag på fakturan.",
    h1: "Flytthjälp Lidingö",
  },

  hero: {
    heading: "Flytthjälp på Lidingö",
    intro:
      "Flyttar du till ön, från ön eller mellan två adresser på Lidingö? Vi kommer med skåpbil och bärare till villan i Brevik, lägenheten i Larsberg eller det nya huset i Dalénum och tar allt över Lidingöbron eller bara några kvarter bort. Du väljer paket, ser priset direkt och slipper låna bil.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt: "Ungdomskraft bär möbler vid flytthjälp på Lidingö",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Sedan 2014 har Ungdomskraft burit och kört bohag åt hushåll i hela Stockholms län. På Lidingö betyder det ofta sluttande tomter, källarvåningar och långa trädgårdsgångar – och sextiotalshus i Larsberg där hissen är liten. Våra bärare är vana vid båda.",
    bookYourText: "Boka flytten på Lidingö",
    bookDescText:
      "Ange adressen du lämnar och adressen du flyttar till, välj paket och tid. Priset visas innan du bekräftar.",
    vehicleTitleText: "Skåpbil, filtar och bärare",
    vehicleDescText:
      "Vi kommer med skåpbil, möbelfiltar och spännband. Du behöver inte hyra bil eller köra själv över bron – vi lastar, kör och lossar.",
  },

  itemsSection: {
    heading: "Det här flyttar vi ofta på Lidingö",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Soffgrupper och fåtöljer, inlindade innan de bärs i trappor och trädgårdsgångar",
        image: "/sofa.webp",
        alt: "Soffa som bärs vid villaflytt i Brevik, Lidingö",
      },
      {
        name: "Sängar & madrasser",
        description: "Kontinentalsängar, ramsängar och barnsängar",
        image: "/bed.webp",
        alt: "Säng som flyttas från lägenhet i Larsberg, Lidingö",
      },
      {
        name: "Matbord & stolar",
        description: "Stora matbord, utdragsbord och stolar",
        image: "/table.webp",
        alt: "Matbord som bärs ut vid flytt på Lidingö",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskin, torktumlare, kyl och frys",
        image: "/washer.webp",
        alt: "Tvättmaskin som flyttas från villa i Käppala, Lidingö",
      },
      {
        name: "Garderober & byråer",
        description: "Garderober, byråer och bokhyllor – isärtagna om det behövs",
        image: "/wardrobe.webp",
        alt: "Byrå som lastas i skåpbil vid flytt på Lidingö",
      },
      {
        name: "Flyttkartonger",
        description: "Kartonger med böcker, porslin, kläder och förrådssaker",
        image: "/box.webp",
        alt: "Flyttkartonger som bärs in i nybyggd lägenhet i Dalénum, Lidingö",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp på Lidingö – villor, sextiotalshus och nya kvarter",
    mainText:
      "Lidingö är en ö med 28 kommundelar, och bostäderna skiljer sig mycket åt från del till del. I Brevik, Käppala, Gåshaga, Sticklinge, Elfvik och Hersby är det mest villor, många på kuperade tomter med trappor, källarvåning och en bit att gå från gatan till entrén. Larsberg byggdes på 1960-talet med höga lamellhus och stora gårdar. Dalénum på det gamla AGA-området och kvarteren kring Lidingö centrum är nyare, med hiss men ofta med garage och lastplatser som man behöver veta var de ligger. Vid bokningen frågar vi om våningar, hiss och gångväg, så att rätt antal bärare kommer.\n\nEn vanlig flytt på Lidingö går åt ena eller andra hållet över Lidingöbron: från en lägenhet på Östermalm eller Gärdet ut till ett hus på ön, eller från en villa som blivit för stor till en lägenhet i centrum eller i Stockholm. Avståndet är kort – från Ropsten är man på ön på några minuter – så det mesta av tiden går till att bära och att lasta rätt. En villa har också mer än man tror i förråd, källare och garage, så välj ett paket som räcker till hela huset.\n\nLidingöbanan går från Ropsten över Torsvik, Larsberg, Aga, Skärsätra och Käppala ut till Gåshaga, och Norra och Södra Kungsvägen binder ihop öns olika delar. Vi planerar rutten efter det och efter var bilen kan stå: på smala villavägar backar vi gärna in på uppfarten, och i de större lägenhetsområdena frågar vi efter lastplats eller gård. Har du en tomt som sluttar ner mot vattnet, berätta hur långt det är att bära – då räknar vi med det.",
    preparationHeading: "Förbered flytten på Lidingö",
    preparationText:
      "Packa lösa saker i kartonger och märk dem med rum. Töm garderober och byråer, frosta av frysen dagen innan och ta isär det du själv enkelt kan skruva loss – annars gör vi det. Glöm inte källaren, garaget och eventuellt båthus eller förråd på tomten; det är där det brukar ta längst tid.\n\nSe till att uppfarten är fri och att grindar går att öppna. Bor du på en smal gata i Hersby eller Käppala, säg till om det är svårt att vända en skåpbil. I Larsberg och Dalénum: ta reda på om hissen går att låsa och var närmaste lastplats finns. Ha portkoder, garageöppnare och nycklar redo för båda adresserna.",
    additionalSections: [
      {
        heading: "Kommundelar vi flyttar i på Lidingö",
        content:
          "Vi tar flyttuppdrag på hela ön: Lidingö centrum, Torsvik, Baggeby, Bodal, Larsberg, Dalénum, Hersby, Sticklinge, Rudboda, Elfvik, Käppala, Skärsätra, Brevik och Gåshaga med flera.\n\nVi flyttar också mellan Lidingö och resten av länet – till och från innerstaden, Danderyd, Täby, Nacka och Solna är vanliga sträckor. Körsträcka inom 20 km ingår i paketpriset, och längre sträckor räknas per kilometer. Du ser hela beloppet innan du bokar.",
      },
      {
        heading: "Villaflytt och flytt till mindre boende",
        content:
          "Många av våra uppdrag på Lidingö gäller ett helt hus – ibland för en familj som flyttar in, ibland för någon som flyttar från villan till lägenhet efter många år. Det senare innebär oftast att en del ska med, en del ska till återvinning och en del ska skänkas. Vi kan ta flytten och bortforslingen i samma veva om du bokar båda, så att huset blir tomt på en gång.",
        cta: { text: "Se bortforsling på Lidingö →", href: "/bortforsling-lidingo" },
      },
      {
        heading: "Varför boka flytthjälp på Lidingö med Ungdomskraft?",
        listItems: [
          {
            label: "Vana vid öns villor –",
            text: "Sluttande tomter, källarvåningar och långa gångar från gatan är vardag för oss på Lidingö.",
          },
          {
            label: "Nära till stan –",
            text: "Via Lidingöbron är vi snabbt på plats, och en flytt mellan ön och innerstaden ryms oftast inom de 20 km som ingår.",
          },
          {
            label: "Priset syns direkt –",
            text: "Du väljer paket och ser beloppet innan du bokar. Ingen offert, inget löpande timpris.",
          },
          {
            label: "Försäkrat genom Trygg Hansa –",
            text: "Dina möbler och fastigheten är försäkrade under hela uppdraget.",
          },
          {
            label: "RUT-avdrag direkt –",
            text: "Vi drar av 50 % av arbetskostnaden på fakturan och sköter ansökan hos Skatteverket.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp på Lidingö?",
      answer:
        "Det beror på hur mycket som ska flyttas. Liten (995 kr) räcker för 1–2 föremål, Mellan (1 495 kr) för 3–6 och Stor (2 495 kr) för 7–10. För ett helt hem finns Extra Stor (4 995 kr, upp till 4 h) och Mega Stor (8 995 kr, upp till 8 h). Körsträcka inom 20 km ingår. Du ser priset direkt vid bokning.",
    },
    {
      question: "Kostar det extra att flytta över Lidingöbron?",
      answer:
        "Nej, bron i sig kostar inget extra. Det som räknas är körsträckan: inom 20 km ingår i paketet, och en flytt mellan Lidingö och innerstaden brukar rymmas där.",
    },
    {
      question: "Får jag RUT-avdrag på flytthjälp på Lidingö?",
      answer:
        "Ja. Du får 50 % avdrag på arbetskostnaden, upp till 75 000 kr per person och år, och vi drar av det direkt på fakturan.",
    },
    {
      question: "Kan ni bära på sluttande villatomter?",
      answer:
        "Ja, det är vanligt på Lidingö. Berätta vid bokningen om det är trappor eller en lång gång från gatan till huset, så räknar vi med tiden.",
    },
    {
      question: "Hjälper ni till när man flyttar från villa till lägenhet?",
      answer:
        "Ja. Vi flyttar det som ska med och kan samtidigt köra bort det som inte får plats, om du bokar bortforsling också.",
    },
    {
      question: "Flyttar ni i höghusen i Larsberg?",
      answer:
        "Ja. De höga husen i Larsberg har hiss, men stora möbler går inte alltid in i den. Då bär vi i trappan. Ange våning när du bokar.",
    },
    {
      question: "Hur snabbt kan ni komma till Lidingö?",
      answer:
        "Ofta inom några dagar. I bokningen ser du vilka tider som är lediga för dina adresser.",
    },
    {
      question: "Ingår skåpbil?",
      answer:
        "Ja, från paketet Liten och uppåt ingår två personer och skåpbil.",
    },
    {
      question: "Flyttar ni vitvaror?",
      answer:
        "Ja, tvättmaskin, torktumlare, kyl och frys. Frosta av frysen och töm kylen dagen före.",
    },
    {
      question: "Tar ni isär och sätter ihop möbler?",
      answer:
        "Ja, sängar, garderober och bord som inte går hela genom dörrarna tar vi isär och sätter ihop igen.",
    },
    {
      question: "Kan ni flytta en helg?",
      answer:
        "Ja, vi flyttar alla veckodagar, även lördag och söndag.",
    },
    {
      question: "Tar ni med saker från båthus eller förråd på tomten?",
      answer:
        "Ja, så länge det går att bära till bilen. Säg till vid bokningen så att paketet räcker till allt.",
    },
    {
      question: "Vilka kommundelar på Lidingö täcker ni?",
      answer:
        "Hela ön, bland annat Lidingö centrum, Torsvik, Baggeby, Bodal, Larsberg, Dalénum, Hersby, Sticklinge, Rudboda, Elfvik, Käppala, Skärsätra, Brevik och Gåshaga.",
    },
    {
      question: "Vilka kommuner utanför Lidingö flyttar ni i?",
      answer: `Hela Stockholmsregionen: ${stockholmKommunerText}. Samma prismodell överallt – paketpris plus körsträcka över 20 km.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp på Lidingö i 3 steg",
    step2Text:
      "Bärarna kommer med skåpbil, bär ut från huset eller lägenheten och lastar stadigt. Sedan kör vi till nya adressen, på ön eller över bron.",
    step3Title: "Allt inburet",
    step3Desc:
      "Möbler och kartonger bärs in i rätt rum och det vi tog isär sätts ihop. Nya hemmet är klart att packa upp i.",
    ctaHeading: "Boka flytthjälp på Lidingö",
    ctaText:
      "Skriv in adresserna, välj paket och tid. Priset visas direkt, med bil och bärare.",
  },

  en: {
    slug: "moving-help-lidingo",
    meta: {
      title: "Moving Help Lidingö – Van & Crew | Ungdomskraft",
      description:
        "Book moving help on Lidingö. We move villas and flats in Brevik, Käppala, Larsberg, Hersby and Gåshaga. Fixed package price and RUT deduction on the invoice.",
      h1: "Moving Help Lidingö",
    },
    hero: {
      heading: "Moving Help on Lidingö",
      intro:
        "Moving to the island, off the island or between two addresses on Lidingö? We arrive with a van and carriers at the villa in Brevik, the flat in Larsberg or the new home in Dalénum and take everything across the Lidingö bridge or just a few blocks away. Choose a package, see the price straight away and skip borrowing a car.",
      heroImageAlt: "Ungdomskraft carrying furniture during a move on Lidingö",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Since 2014 Ungdomskraft has carried and driven households across Stockholm County. On Lidingö that often means sloping plots, basement floors and long garden paths – and 1960s blocks in Larsberg with small lifts. Our carriers are used to both.",
      bookYourText: "Book your Lidingö move",
      bookDescText:
        "Enter the address you are leaving and the one you are moving to, choose a package and time. The price is shown before you confirm.",
      vehicleTitleText: "Van, blankets and carriers",
      vehicleDescText:
        "We bring a van, furniture blankets and straps. No need to rent a vehicle or drive across the bridge yourself – we load, drive and unload.",
    },
    itemsSection: {
      heading: "What we often move on Lidingö",
      items: [
        { name: "Sofas & armchairs", description: "Sofa sets and armchairs, wrapped before being carried on stairs and garden paths", image: "/sofa.webp", alt: "Sofa carried during a villa move in Brevik, Lidingö" },
        { name: "Beds & mattresses", description: "Continental beds, bed frames and children's beds", image: "/bed.webp", alt: "Bed moved from a flat in Larsberg, Lidingö" },
        { name: "Dining tables & chairs", description: "Large dining tables, extending tables and chairs", image: "/table.webp", alt: "Dining table carried out during a move on Lidingö" },
        { name: "Appliances", description: "Washing machine, tumble dryer, fridge and freezer", image: "/washer.webp", alt: "Washing machine moved from a villa in Käppala, Lidingö" },
        { name: "Wardrobes & dressers", description: "Wardrobes, dressers and bookcases – taken apart if needed", image: "/wardrobe.webp", alt: "Dresser loaded into a van during a move on Lidingö" },
        { name: "Moving boxes", description: "Boxes with books, china, clothes and storage items", image: "/box.webp", alt: "Moving boxes carried into a new flat in Dalénum, Lidingö" },
      ],
    },
    seoContent: {
      mainHeading: "Moving help on Lidingö – villas, 1960s blocks and new quarters",
      mainText:
        "Lidingö is an island with 28 districts, and housing varies a lot from one to the next. Brevik, Käppala, Gåshaga, Sticklinge, Elfvik and Hersby are mostly villas, many on hilly plots with steps, a basement floor and some distance from the street to the door. Larsberg was built in the 1960s with tall slab blocks and large courtyards. Dalénum on the old AGA site and the quarters around Lidingö centrum are newer, with lifts but often with garages and loading spots you need to know the way to. When you book we ask about floors, lifts and walking distance, so the right number of carriers turns up.\n\nA common Lidingö move goes one way or the other across the Lidingö bridge: from a flat in Östermalm or Gärdet out to a house on the island, or from a villa that has become too big to a flat in the centre or in Stockholm. The distance is short – from Ropsten you are on the island in minutes – so most of the time goes to carrying and loading properly. A villa also holds more than you expect in storage, basement and garage, so choose a package that covers the whole house.\n\nThe Lidingöbanan light rail runs from Ropsten via Torsvik, Larsberg, Aga, Skärsätra and Käppala out to Gåshaga, and Norra and Södra Kungsvägen tie the island together. We plan the route around that and around where the van can stand: on narrow villa roads we like to reverse onto the driveway, and in the larger apartment areas we ask about a loading spot or courtyard. If your plot slopes down to the water, tell us how far it is to carry – then we plan for it.",
      preparationHeading: "Prepare your move on Lidingö",
      preparationText:
        "Pack loose items in boxes and label them by room. Empty wardrobes and dressers, defrost the freezer the day before and take apart what you can easily unscrew yourself – otherwise we will. Don't forget the basement, garage and any boathouse or shed on the plot; that is usually where most of the time goes.\n\nMake sure the driveway is clear and gates can be opened. If you live on a narrow road in Hersby or Käppala, tell us if it is hard to turn a van. In Larsberg and Dalénum, find out whether the lift can be locked and where the nearest loading spot is. Have door codes, garage openers and keys ready for both addresses.",
      additionalSections: [
        {
          heading: "Districts we move in on Lidingö",
          content:
            "We take moving jobs across the whole island: Lidingö centrum, Torsvik, Baggeby, Bodal, Larsberg, Dalénum, Hersby, Sticklinge, Rudboda, Elfvik, Käppala, Skärsätra, Brevik, Gåshaga and more.\n\nWe also move between Lidingö and the rest of the county – to and from central Stockholm, Danderyd, Täby, Nacka and Solna are common routes. Driving within 20 km is included in the package price, and longer distances are charged per kilometre. You see the full amount before you book.",
        },
        {
          heading: "Villa moves and moving to a smaller home",
          content:
            "Many of our jobs on Lidingö involve a whole house – sometimes for a family moving in, sometimes for someone leaving the villa for a flat after many years. The latter usually means some things come along, some go to recycling and some are donated. We can do the move and the junk removal at the same time if you book both, so the house is emptied in one go.",
          cta: { text: "See junk removal on Lidingö →", href: "/en/junk-removal-lidingo" },
        },
        {
          heading: "Why book moving help on Lidingö with Ungdomskraft?",
          listItems: [
            { label: "Used to the island's villas –", text: "Sloping plots, basement floors and long paths from the street are everyday work for us on Lidingö." },
            { label: "Close to town –", text: "Via the Lidingö bridge we are quickly on site, and a move between the island and central Stockholm usually fits within the 20 km included." },
            { label: "Price shown up front –", text: "You choose a package and see the amount before you book. No quote, no running hourly rate." },
            { label: "Insured through Trygg Hansa –", text: "Your furniture and the property are insured throughout the job." },
            { label: "RUT deduction applied –", text: "We deduct 50% of the labour cost on the invoice and handle the claim with the Swedish Tax Agency." },
          ],
        },
      ],
    },
    faq: [
      { question: "How much does moving help on Lidingö cost?", answer: "It depends on how much is moving. Small (SEK 995) covers 1–2 items, Medium (SEK 1,495) 3–6 and Large (SEK 2,495) 7–10. For a whole home there is Extra Large (SEK 4,995, up to 4 h) and Mega Large (SEK 8,995, up to 8 h). Driving within 20 km is included. You see the price when booking." },
      { question: "Does crossing the Lidingö bridge cost extra?", answer: "No, the bridge itself costs nothing extra. What counts is the driving distance: 20 km is included in the package, and a move between Lidingö and central Stockholm usually fits within that." },
      { question: "Do I get a RUT deduction on moving help on Lidingö?", answer: "Yes. You get 50% off the labour cost, up to SEK 75,000 per person per year, deducted directly on the invoice." },
      { question: "Can you carry on sloping villa plots?", answer: "Yes, it's common on Lidingö. Tell us when booking if there are steps or a long path from the street to the house, and we allow time for it." },
      { question: "Do you help when moving from a villa to a flat?", answer: "Yes. We move what comes along and can take away what doesn't fit at the same time, if you also book junk removal." },
      { question: "Do you move in the tall blocks in Larsberg?", answer: "Yes. The tall blocks in Larsberg have lifts, but large furniture doesn't always fit. Then we use the stairs. State the floor when booking." },
      { question: "How soon can you come to Lidingö?", answer: "Often within a few days. The booking shows which times are free for your addresses." },
      { question: "Is a van included?", answer: "Yes, from the Small package upwards two people and a van are included." },
      { question: "Do you move appliances?", answer: "Yes – washing machine, tumble dryer, fridge and freezer. Defrost the freezer and empty the fridge the day before." },
      { question: "Do you take apart and reassemble furniture?", answer: "Yes, beds, wardrobes and tables that won't fit through doors whole are taken apart and put back together." },
      { question: "Can you move at the weekend?", answer: "Yes, we move every day of the week, including Saturday and Sunday." },
      { question: "Will you take things from a boathouse or shed on the plot?", answer: "Yes, as long as it can be carried to the van. Mention it when booking so the package covers everything." },
      { question: "Which districts on Lidingö do you cover?", answer: "The whole island, including Lidingö centrum, Torsvik, Baggeby, Bodal, Larsberg, Dalénum, Hersby, Sticklinge, Rudboda, Elfvik, Käppala, Skärsätra, Brevik and Gåshaga." },
      { question: "Which municipalities outside Lidingö do you serve?", answer: `The whole Stockholm region: ${stockholmKommunerText}. Same pricing everywhere – package price plus driving beyond 20 km.` },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help on Lidingö in 3 steps",
      step2Text:
        "The carriers arrive with a van, carry out of the house or flat and load securely. Then we drive to the new address, on the island or across the bridge.",
      step3Title: "Everything carried in",
      step3Desc:
        "Furniture and boxes are carried into the right rooms and anything we took apart is reassembled. Your new home is ready to unpack.",
      ctaHeading: "Book moving help on Lidingö",
      ctaText:
        "Enter the addresses, choose a package and time. The price is shown straight away, with van and carriers.",
    },
  },
};
